import React, { useState } from 'react';
import { FiAlertCircle as AlertCircle, FiCheckCircle as CheckCircle, FiUser as User } from 'react-icons/fi';

interface ValidationResult {
  success: boolean;
  [key: string]: unknown;
}

interface UserValidationStepProps {
  currentUserEmail: string;
  currentUserName: string;
  onValidate: (email: string, name: string) => Promise<ValidationResult | null>;
  loading?: boolean;
  error?: string | null;
}

const UserValidationStep: React.FC<UserValidationStepProps> = ({
  currentUserEmail,
  currentUserName,
  onValidate,
  loading = false,
  error = null,
}) => {
  const [isSameUser, setIsSameUser] = useState<boolean | null>(null);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const handleSameUserConfirm = async () => {
    setIsSameUser(true);
    try {
      const result = await onValidate(currentUserEmail, currentUserName);
      setValidationResult(result);
    } catch {
      // Parent surfaces the error via the `error` prop. Reset to the choice
      // screen so the user can retry or pick the other path.
      setIsSameUser(null);
    }
  };

  const handleDifferentUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSameUser(false);
    try {
      const result = await onValidate(newUserEmail, newUserName);
      setValidationResult(result);
    } catch {
      // Parent surfaces the error via the `error` prop. Keep the form
      // visible so the user sees the error inline with their input.
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
          <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Who&apos;s Starting This Trial?</h2>
        <p className="text-gray-600 dark:text-gray-400">Help us set up your account correctly</p>
      </div>

      {/* Initial Choice - Same User or Different User */}
      {isSameUser === null && (
        <div className="space-y-4">
          {/* Same User Option */}
          <button
            onClick={handleSameUserConfirm}
            disabled={loading}
            className="w-full p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                  <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {currentUserName || 'This is me'}
                </h3>
                <div className="bg-gray-100 dark:bg-gray-700 rounded-md px-3 py-2 inline-block">
                  <p className="text-sm font-mono text-gray-700 dark:text-gray-300">{currentUserEmail}</p>
                </div>
              </div>
            </div>
          </button>

          {/* Different User Option */}
          <button
            onClick={() => setIsSameUser(false)}
            disabled={loading}
            className="w-full p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                  <User className="w-6 h-6 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">I&apos;m someone else</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  A different person is setting up this trial (you&apos;ll become the Account Owner)
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Same-user in flight — show a spinner so the screen isn't empty
          while the parent calls /auth/validate-trial-user and
          /clients/upgrade-to-trial. On error the parent's `error` prop
          renders below; handleSameUserConfirm also resets isSameUser to
          null so the choice cards come back. */}
      {isSameUser === true && !validationResult && loading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Setting up your trial…</span>
        </div>
      )}

      {/* Different User Form */}
      {isSameUser === false && !validationResult && (
        <form onSubmit={handleDifferentUserSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              placeholder="John Doe"
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Your Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              placeholder="you@company.com"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !newUserEmail.trim() || !newUserName.trim()}
            className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span>Continue</span>
            )}
          </button>
        </form>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserValidationStep;
